import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Check, X, Sparkles, Crown, Building2, Rocket, 
  ChevronDown, ChevronUp, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pricing tiers data
const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 0,
    yearlyPrice: 0,
    description: 'Essential tools to run your business',
    icon: Sparkles,
    color: 'zinc',
    popular: false,
    features: {
      'Job Management': [
        'Manage Customer Details',
        'Add Unlimited Customers',
        'Group Jobs into Rounds',
        'Replan Jobs',
        'Assign work to Users and Teams',
        'User Roles',
        'Real-time Worker Updates',
        'Optimised Route Navigation'
      ],
      'Business Management': [
        'Take Payments Automatically',
        'Record Payments',
        'Create & Send Quotes',
        'Send Invoices Automatically',
        'Record Expenses'
      ],
      'Customer Services': [
        'Send Job Status Notifications',
        'Send Appointment Reminders'
      ],
      'Messaging': [
        'Customise Message Templates',
        'Email Messaging'
      ],
      'Platform': [
        'Offline Mode',
        'Desktop & Mobile App',
        'Multi-user',
        'Daily back-up'
      ]
    }
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 0,
    yearlyPrice: 0,
    description: 'Advanced tools to optimise efficiency',
    icon: Rocket,
    color: 'blue',
    popular: true,
    credits: 150,
    features: {
      'Job Management': [
        'Everything in Starter',
        'Attach Job Files and Pictures'
      ],
      'Business Management': [
        'Everything in Starter',
        'Point of Sale Payments',
        'Apply a discount',
        'On Demand Invoicing'
      ],
      'Customer Services': [
        'Portal Website',
        'Update Payment Details',
        'Customer Replies'
      ],
      'Messaging': [
        'Everything in Starter',
        'Receive Push Notifications',
        'Send Bulk Messages'
      ],
      'Financial': [
        'Reconcile Bank Accounts',
        'Time Tracking Billing Report'
      ]
    }
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 0,
    yearlyPrice: 0,
    description: 'Ultimate features for business growth',
    icon: Crown,
    color: 'purple',
    popular: false,
    credits: 150,
    features: {
      'Job Management': [
        'Everything in Growth',
        'View and Plan Jobs on a Map',
        'Required Skills for a Job',
        'Bulk Price Changes',
        'Custom Job Checklists',
        'Job Time Recording'
      ],
      'Business Management': [
        'Everything in Growth',
        'Customer Statements',
        'Manage Suppliers',
        'Invoice Attachments'
      ],
      'Customer Services': [
        'Everything in Growth',
        'Review Job Appointments',
        'Live Chat',
        'Update Customer Profile',
        'Request Quote'
      ],
      'Messaging': [
        'Everything in Growth',
        'Send Marketing Campaigns',
        'Create Custom Audiences',
        'Private Number Messaging'
      ],
      'Financial': [
        'Everything in Growth',
        'Send HMRC VAT Returns',
        'Multiple Bank Accounts',
        'Import Transactions',
        'Profit Loss Reports'
      ]
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 0,
    yearlyPrice: 0,
    description: 'Infinitely scalable for large businesses',
    icon: Building2,
    color: 'emerald',
    popular: false,
    credits: 150,
    features: {
      'Job Management': [
        'Everything in Professional',
        'Route Optimisation',
        'Calendar Integration'
      ],
      'Business Management': [
        'Everything in Professional'
      ],
      'Customer Services': [
        'Everything in Professional',
        'Book Appointment Online',
        'Pay Balance Online',
        'Cancel Appointment'
      ],
      'Messaging': [
        'Everything in Professional'
      ],
      'Financial': [
        'Everything in Professional',
        'Chart of Accounts Report'
      ],
      'Enterprise Features': [
        'Dedicated Account Manager',
        'Priority Support',
        'Custom Integrations',
        'API Access',
        'SLA Guarantee'
      ]
    }
  }
];

const PricingPage = ({ onBack, currentTier = 'starter' }) => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [expandedTier, setExpandedTier] = useState(null);
  const [upgrading, setUpgrading] = useState(null);

  const getColorClasses = (color, isCurrentTier) => {
    const colors = {
      zinc: { bg: 'bg-zinc-100', text: 'text-zinc-600', border: 'border-zinc-200', accent: 'bg-zinc-500' },
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', accent: 'bg-blue-500' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', accent: 'bg-purple-500' },
      emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', accent: 'bg-emerald-500' }
    };
    return colors[color] || colors.zinc;
  };

  const handleUpgrade = async (tierId) => {
    setUpgrading(tierId);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscription_tier: tierId })
      });
      
      if (response.ok) {
        // Refresh page or update state
        window.location.reload();
      }
    } catch (error) {
      console.error('Error upgrading:', error);
    } finally {
      setUpgrading(null);
    }
  };

  const tierIndex = (id) => TIERS.findIndex(t => t.id === id);
  const isUpgrade = (tierId) => tierIndex(tierId) > tierIndex(currentTier);
  const isDowngrade = (tierId) => tierIndex(tierId) < tierIndex(currentTier);
  const isCurrent = (tierId) => tierId === currentTier;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-zinc-100 rounded-xl transition-colors"
            >
              <ArrowLeft size={20} className="text-zinc-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">Plans & Pricing</h1>
              <p className="text-sm text-zinc-500">Choose the plan that fits your business</p>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Toggle */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center gap-4 mb-8">
          <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-zinc-900' : 'text-zinc-500'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
            className="relative w-14 h-7 bg-zinc-200 rounded-full transition-colors"
            style={{ backgroundColor: billingPeriod === 'yearly' ? '#10b981' : '#e4e4e7' }}
          >
            <div
              className="absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all"
              style={{ left: billingPeriod === 'yearly' ? '30px' : '4px' }}
            />
          </button>
          <span className={`text-sm font-medium ${billingPeriod === 'yearly' ? 'text-zinc-900' : 'text-zinc-500'}`}>
            Yearly
            <span className="ml-1 text-emerald-600 text-xs">(Save 2 months)</span>
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((tier) => {
            const colors = getColorClasses(tier.color);
            const Icon = tier.icon;
            const current = isCurrent(tier.id);
            const upgrade = isUpgrade(tier.id);
            const downgrade = isDowngrade(tier.id);

            return (
              <div
                key={tier.id}
                className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all ${
                  current ? 'border-emerald-500 shadow-lg shadow-emerald-100' : 
                  tier.popular ? 'border-blue-400 shadow-lg' : 'border-zinc-200 hover:border-zinc-300'
                }`}
              >
                {tier.popular && !current && (
                  <div className="absolute top-0 left-0 right-0 bg-blue-500 text-white text-xs font-semibold py-1 text-center">
                    MOST POPULAR
                  </div>
                )}
                {current && (
                  <div className="absolute top-0 left-0 right-0 bg-emerald-500 text-white text-xs font-semibold py-1 text-center">
                    YOUR CURRENT PLAN
                  </div>
                )}

                <div className={`p-6 ${(tier.popular || current) ? 'pt-10' : ''}`}>
                  {/* Icon & Name */}
                  <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon size={24} className={colors.text} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-zinc-900">{tier.name}</h3>
                  <p className="text-sm text-zinc-500 mb-4">{tier.description}</p>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      {tier.price === 0 ? (
                        <>
                          <span className="text-4xl font-bold text-emerald-600">FREE</span>
                          <span className="text-zinc-500 text-sm ml-1">(Beta)</span>
                        </>
                      ) : (
                        <>
                          <span className="text-4xl font-bold text-zinc-900">
                            £{billingPeriod === 'monthly' ? tier.price.toFixed(2) : (tier.yearlyPrice / 12).toFixed(2)}
                          </span>
                          <span className="text-zinc-500">/user/mo</span>
                        </>
                      )}
                    </div>
                    {tier.price > 0 && billingPeriod === 'yearly' && (
                      <p className="text-sm text-emerald-600 mt-1">
                        £{tier.yearlyPrice}/year (billed annually)
                      </p>
                    )}
                    {tier.credits && (
                      <p className="text-sm text-zinc-500 mt-2">
                        + {tier.credits} SMS credits/mo
                      </p>
                    )}
                  </div>

                  {/* Action Button */}
                  {current ? (
                    <div className="py-3 text-center text-emerald-600 font-semibold bg-emerald-50 rounded-xl">
                      Current Plan
                    </div>
                  ) : upgrade ? (
                    <button
                      onClick={() => handleUpgrade(tier.id)}
                      disabled={upgrading === tier.id}
                      className={`w-full py-3 ${colors.accent} text-white font-semibold rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                    >
                      {upgrading === tier.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        'Upgrade'
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(tier.id)}
                      disabled={upgrading === tier.id}
                      className="w-full py-3 border-2 border-zinc-200 text-zinc-600 font-semibold rounded-xl hover:bg-zinc-50 transition-colors flex items-center justify-center gap-2"
                    >
                      {upgrading === tier.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        'Downgrade'
                      )}
                    </button>
                  )}
                </div>

                {/* Features Preview */}
                <div className="border-t border-zinc-100 p-6">
                  <button
                    onClick={() => setExpandedTier(expandedTier === tier.id ? null : tier.id)}
                    className="w-full flex items-center justify-between text-sm font-medium text-zinc-700"
                  >
                    <span>View all features</span>
                    {expandedTier === tier.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>

                  {expandedTier === tier.id && (
                    <div className="mt-4 space-y-4">
                      {Object.entries(tier.features).map(([category, features]) => (
                        <div key={category}>
                          <h4 className="font-medium text-zinc-800 text-sm mb-2">{category}</h4>
                          <ul className="space-y-1.5">
                            {features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-zinc-600">
                                <Check size={14} className={`${colors.text} mt-0.5 flex-shrink-0`} />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison Table - Desktop */}
        <div className="mt-16 hidden lg:block">
          <h2 className="text-2xl font-bold text-zinc-900 mb-8 text-center">Compare Plans</h2>
          
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="py-4 px-6 text-left text-sm font-semibold text-zinc-700">Feature</th>
                  {TIERS.map(tier => (
                    <th key={tier.id} className={`py-4 px-6 text-center text-sm font-semibold ${
                      isCurrent(tier.id) ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700'
                    }`}>
                      {tier.name}
                      {isCurrent(tier.id) && <span className="ml-1 text-xs">(Current)</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-zinc-100">
                  <td className="py-3 px-6 text-sm text-zinc-700">Monthly Price (per user)</td>
                  {TIERS.map(tier => (
                    <td key={tier.id} className={`py-3 px-6 text-center text-sm font-semibold ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {tier.price === 0 ? <span className="text-emerald-600">Free</span> : `£${tier.price.toFixed(2)}`}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-zinc-100">
                  <td className="py-3 px-6 text-sm text-zinc-700">SMS Credits</td>
                  {TIERS.map(tier => (
                    <td key={tier.id} className={`py-3 px-6 text-center text-sm ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {tier.credits ? `${tier.credits}/mo` : '-'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-zinc-100">
                  <td className="py-3 px-6 text-sm text-zinc-700">Job Files & Pictures</td>
                  {TIERS.map((tier, idx) => (
                    <td key={tier.id} className={`py-3 px-6 text-center ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {idx >= 1 ? <Check size={18} className="mx-auto text-emerald-500" /> : <X size={18} className="mx-auto text-zinc-300" />}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-zinc-100">
                  <td className="py-3 px-6 text-sm text-zinc-700">Customer Portal</td>
                  {TIERS.map((tier, idx) => (
                    <td key={tier.id} className={`py-3 px-6 text-center ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {idx >= 1 ? <Check size={18} className="mx-auto text-emerald-500" /> : <X size={18} className="mx-auto text-zinc-300" />}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-zinc-100">
                  <td className="py-3 px-6 text-sm text-zinc-700">Bank Reconciliation</td>
                  {TIERS.map((tier, idx) => (
                    <td key={tier.id} className={`py-3 px-6 text-center ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {idx >= 1 ? <Check size={18} className="mx-auto text-emerald-500" /> : <X size={18} className="mx-auto text-zinc-300" />}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-zinc-100">
                  <td className="py-3 px-6 text-sm text-zinc-700">Map Planning</td>
                  {TIERS.map((tier, idx) => (
                    <td key={tier.id} className={`py-3 px-6 text-center ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {idx >= 2 ? <Check size={18} className="mx-auto text-emerald-500" /> : <X size={18} className="mx-auto text-zinc-300" />}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-zinc-100">
                  <td className="py-3 px-6 text-sm text-zinc-700">Marketing Campaigns</td>
                  {TIERS.map((tier, idx) => (
                    <td key={tier.id} className={`py-3 px-6 text-center ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {idx >= 2 ? <Check size={18} className="mx-auto text-emerald-500" /> : <X size={18} className="mx-auto text-zinc-300" />}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-zinc-100">
                  <td className="py-3 px-6 text-sm text-zinc-700">VAT Returns</td>
                  {TIERS.map((tier, idx) => (
                    <td key={tier.id} className={`py-3 px-6 text-center ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {idx >= 2 ? <Check size={18} className="mx-auto text-emerald-500" /> : <X size={18} className="mx-auto text-zinc-300" />}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-3 px-6 text-sm text-zinc-700">Dedicated Account Manager</td>
                  {TIERS.map((tier, idx) => (
                    <td key={tier.id} className={`py-3 px-6 text-center ${
                      isCurrent(tier.id) ? 'bg-emerald-50' : ''
                    }`}>
                      {idx >= 3 ? <Check size={18} className="mx-auto text-emerald-500" /> : <X size={18} className="mx-auto text-zinc-300" />}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-zinc-900 mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {[
              {
                q: 'Can I change my plan anytime?',
                a: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately and billing is prorated.'
              },
              {
                q: 'Is there a free trial?',
                a: 'Yes, all new accounts start with a 14-day free trial of the Growth plan. No credit card required.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards (Visa, Mastercard, Amex) and can set up Direct Debit for annual plans.'
              },
              {
                q: 'Can I add more users?',
                a: 'Absolutely! You can add unlimited users on any plan. Each additional user is charged at your plan\'s per-user rate.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-zinc-200 p-6">
                <h3 className="font-semibold text-zinc-900 mb-2">{faq.q}</h3>
                <p className="text-zinc-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
